import React, { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from 'react-dropzone';
import { RadioButton, InputField, InputArea, Button } from "elements";

import {
  Wrapper,
  List,
  Hr,
  Content,
  Footer,
  ListPlaceholder,
  Form,
  FormFooter,
  FormTypes,
  FileUploadBox
} from "./styled";

import FileItem, { Type } from "./Item";

import { CreateOptionFile, DeleteOptionFile, GetOptionFiles } from "redux/DecisionMatrix/OptionFiles/action";

import { isValidUrl } from "utils/helper";
import Notify from "utils/notify";

interface IProps {
  optionId: number;
  onClose: () => void;
}

const DocumentsList: FC<IProps> = ({ optionId, onClose }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedType, setSelected] = useState<Type>(Type.File);
  const [formState, setFormState] = useState<{ fileNames?: string[]; link?: string; note?: string }>({});

  // @ts-ignore
  const { data, loading } = useSelector((state: { optionFilesReducer: { data: IItem[]; loading: boolean } }) => state.optionFilesReducer);

  useEffect(() => {
    dispatch(GetOptionFiles(optionId));
  }, [optionId]);

  // react-dropzone hook
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const fileNames = acceptedFiles.map(file => file.name);

      setFormState((prev) => ({ ...prev, fileNames }));

      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        acceptedFiles.forEach(file => dataTransfer.items.add(file));
        fileInputRef.current.files = dataTransfer.files;
      }
    },
    onDropRejected: (fileRejections) => {
      const firstRejection = fileRejections[0];

      if (firstRejection.errors.some(err => err.code === 'name-too-long')) {
        Notify.error('File name exceeds 100 characters.', false);
      } else {
        Notify.error('Invalid file type. Please upload PDF, DOC, DOCX, XLS, or CSV files only.', false);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    validator: (file) => {
      if (file?.name?.length > 100) {
        return {
          code: 'name-too-long',
          message: `File name exceeds 100 characters.`,
        };
      }
      return null; // File is valid
    },
    multiple: true, // Allow multiple files
  });

  const handleRemove = (id: number) => {
    dispatch(DeleteOptionFile(id));
  }

  const handleAddFileClick = () => {
    // Trigger click on the hidden file input
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files) {
      const fileNames = Array.from(files).map(file => file.name);
      setFormState((prev) => ({ ...prev, fileNames }));
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const formData = new FormData();

    switch(selectedType) {
      case Type.File:
        const files = fileInputRef.current?.files;
        if (files) {
          Array.from(files).forEach((file, index) => {
            formData.append(`optionfile[${index}]option`, optionId.toString());
            formData.append(`optionfile[${index}]file`, file);
            formData.append(`optionfile[${index}]type`, Type.File);
          });
          dispatch(CreateOptionFile(formData));
          setFormState({});
          fileInputRef.current.value = '';
        }
        break;
      case Type.Note:
        const noteValue = formState.note?.trim();

        if (!noteValue) {
          Notify.error('Please enter a note.', false);
          return;
        }

        formData.append(`optionfile[0]option`, optionId.toString());
        formData.append(`optionfile[0]note`, (formState.note || '').trim());
        formData.append(`optionfile[0]type`, Type.Note);
        dispatch(CreateOptionFile(formData));

        // Reset form
        setFormState({});
        break;
      case Type.Link:
        formData.append(`optionfile[0]option`, optionId.toString());
        formData.append(`optionfile[0]external_link`, formState.link || '');
        formData.append(`optionfile[0]type`, Type.Link);
        dispatch(CreateOptionFile(formData));

        // Reset form
        setFormState({});
        break;
      default:
        break;
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Object.values(Type).includes(event.target.id as Type)) {
      setSelected(event.target.id as Type);
      setFormState({});
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { name, value } = event.target;

    if (name === Type.Link) {
      // Clear any previous custom validity message
      event.target.setCustomValidity("");

      value = value.trim();

      if (value.length > 2048) {
        // Set a custom validity message for exceeding URL length
        event.target.setCustomValidity("Link cannot be more than 2048 characters.");
      }
      else {
        // Check if the input is a potential URL (contains a valid domain extension)
        const domainRegex = /\.(com|org|net|edu|gov|io|info|biz|us|co|uk)$/i;
  
        if (domainRegex.test(value) && !/^https?:\/\//i.test(value) ) {
          value = `https://${value}`;
        }
  
        if (!isValidUrl(value)) {
          event.target.setCustomValidity("Invalid URL format. Please enter a valid URL.");
        } else {
          event.target.setCustomValidity("");
        }
      }
    }

    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const types = [
    {
      id: Type.File,
      label: 'Document'
    },
    {
      id: Type.Link,
      label: 'Link'
    },
    {
      id: Type.Note,
      label: 'Note'
    }
  ];

  return (
    <Wrapper>
      <FormTypes>
        {types.map((item) => (
          <RadioButton
            id={item.id}
            checked={selectedType === item.id}
            label={item.label}
            name="type"
            rtl
            disabled={loading}
            onChange={handleTypeChange}
            styles={{
              label: {
                alignItems: 'center',
                flexDirection: 'row',
                gap: 6
              }
            }}
            key={item.id}
          />
        ))}
      </FormTypes>
      
      <Form onSubmit={handleSubmit}>
        {selectedType === Type.File && (
          <>
            <FileUploadBox
              {...getRootProps()} // Spread dropzone props for drag-and-drop
              onClick={handleAddFileClick}
              hasFile={!!formState.fileNames?.length}
              isDragActive={isDragActive} // Pass drag state for styling
            >
              <input
                {...getInputProps()} // Spread input props for dropzone
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <span>
                {isDragActive
                  ? 'Drop the files here!'
                  : formState.fileNames?.length
                    ? formState.fileNames.join(', ')
                    : 'Add Documents (PDF, DOC, DOCX, XLS, CSV only)'}
              </span>
            </FileUploadBox>

            <FormFooter>
              {!!formState.fileNames?.length && <Button styleType="primary" type="submit">
                Upload
              </Button>}
            </FormFooter>
          </>
        )}

        {selectedType === Type.Note && (
          <>
            <InputArea
              name={Type.Note}
              value={formState.note || ''}
              placeholder="..."
              max={256}
              counter
              style={{ width: '100%', maxHeight: 180, overflowY: "auto" }}
              allowEnterKey
              onChange={handleInputChange}
            />
            <FormFooter>
              <Button styleType="primary" type="submit" disabled={!formState.note?.length}>
                Add
              </Button>
            </FormFooter>
          </>
        )}

        {selectedType === Type.Link && (
          <>
            <InputField
              name={Type.Link}
              value={formState.link || ''}
              type="url"
              placeholder="https://"
              onChange={handleInputChange}
              parentStyle={{ flex: 1 }}
            />
            <FormFooter>
              <Button styleType="primary" type="submit" disabled={!formState.link?.length}>
                Add
              </Button>
            </FormFooter>
          </>
        )}
      </Form>

      <Hr $mb={0} />

      <Content>
        {data.length ? (
          <>
            <List>
              {data.map((item) => (
                <FileItem item={item} onRemove={handleRemove} key={item.id} />
              ))}
            </List>
          </>
        ) : (
          <ListPlaceholder>
            <span>There are no attachments yet.</span>
          </ListPlaceholder>
        )}
      </Content>

      <Hr $mt={0} />

      <Footer>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          {loading ? "Loading..." : "Close"}
        </Button>
      </Footer>
    </Wrapper>
  );
};

export default DocumentsList;
